import { useContext } from 'react';
import { GraphEditorContext } from '../GraphEditorProvider';
import { useGraphEditorStore, type GraphComment } from '@/graph-store';
import { useHighlightOverlays } from '../hooks/useHighlightOverlays';

/**
 * Component that manages highlight overlays in the workflow graph
 * Aggregates data from the store and calls useHighlightOverlays hook
 * Safe to render inside GraphEditorProvider
 */
export function HighlightOverlays() {
  const ctx = useContext(GraphEditorContext);
  const graph = ctx?.graph ?? null;

  // Get data from store  
  const graphComments = useGraphEditorStore((s) => s.graphData.graphComments);
  const flowEdges = useGraphEditorStore((s) => s.graphData.flowEdges);
  const operationNodes = useGraphEditorStore((s) => s.graphData.operationNodes);

  // Get highlight visibility
  const highlightVisible = useGraphEditorStore((s) => s.highlights.visible);

  // Aggregate comments by stepId (which is the comment field containing edgeId/vertexId)
  const commentsByStep: Record<string, GraphComment[]> = {};
  graphComments.forEach((comment) => {
    // Comments are keyed by stepId which contains the edgeId or vertexId
    if (!commentsByStep[comment.stepId]) {
      commentsByStep[comment.stepId] = [];
    }
    commentsByStep[comment.stepId].push(comment);
  });

  // Aggregate notes, checkpoints, notifications, approvals from operationNodes
  const notesByOperation: Record<string, string> = {};
  const checkpointsByOperation: Record<string, any[]> = {};
  const notificationsByOperation: Record<string, any[]> = {};
  const approversByOperation: Record<string, any[]> = {};

  Object.entries(operationNodes).forEach(([nodeId, node]) => {
    if (node.notes && node.notes.length > 0) {
      notesByOperation[nodeId] = node.notes;
    }
    if (node.checkPoints && node.checkPoints.length > 0) {
      checkpointsByOperation[nodeId] = node.checkPoints;
    }
    // notifications is an object with {notificationType, users}, not an array
    if (node.notifications && Object.keys(node.notifications).length > 0) {
      notificationsByOperation[nodeId] = [node.notifications];
    }
    // approvers is an object with {approverType, users}, not an array
    if (node.approvers && Object.keys(node.approvers).length > 0) {
      approversByOperation[nodeId] = [node.approvers];
    }
  });

  const highlightData = {
    comments: commentsByStep,
    notes: notesByOperation,
    checkpoints: checkpointsByOperation,
    notifications: notificationsByOperation,
    approvals: approversByOperation,
  };

  // Call the hook - it handles null graph gracefully
  useHighlightOverlays(graph, highlightData, highlightVisible, flowEdges, operationNodes);

  return null;
}
